/**
 * Admin in Floodlight (v1.71): type and polish only.
 *
 * Every admin style that changes with the look is written
 * `uiVar(<adm name>, <classic value>)`. Classic never defines --bo-adm-*, so the
 * fallback (the exact pre-Floodlight value) wins and Classic is unchanged.
 *
 * v1.71 (Trev, Oct 19: "keep our current background and glassmorphic style",
 * "our purple and blue accents remain"): Floodlight no longer recolours admin.
 * It defines NO colour, background, border or radius variable, so every one of
 * those uiVar() calls falls back to the original value in both looks. Floodlight
 * contributes only type: Archivo, tabular numerals, caps + tracking on titles,
 * buttons and segment labels, and a purple focus ring / selection.
 *
 * Never put a uiVar where JS concatenates the value (`${c}22`): adminUi keeps
 * literal `A` tokens for that.
 */
import { uiVar, varBlock, registerUiStyleCss, FL_ANY } from '../uiStyleCss';

const ARCHIVO = 'Archivo, "Helvetica Neue", Arial, sans-serif';
/** The original admin accent (adminUi A.accent), used for the focus ring. */
const ACCENT = '#a78bfa';

/** Floodlight values: typography only. Any other --bo-adm-* name stays undefined (classic fallback). */
export const ADMIN_FL_VARS = {
    'adm-display-stretch': '108%',
    'adm-display-case': 'uppercase',
    'adm-display-track': '0.02em',
    'adm-label-track': '0.08em',
    'adm-nums': 'tabular-nums',
};

/** Names Floodlight may define on admin: type only, never a colour/ground/border/shape. */
export const ADMIN_TYPE_VARS = Object.keys(ADMIN_FL_VARS);

/** Var-backed versions of adminUi's `A` palette, for direct style use only (never concatenate). */
export const AV = {
    ink: uiVar('adm-ink', 'rgba(255,255,255,0.92)'),
    soft: uiVar('adm-soft', 'rgba(255,255,255,0.62)'),
    faint: uiVar('adm-faint', 'rgba(255,255,255,0.38)'),
    line: uiVar('adm-line', 'rgba(255,255,255,0.08)'),
    // A.panel is rgba(…,0.035); react-native-web renders literal colours with the
    // alpha rounded to 2 places, and a var() fallback skips that, so the
    // fallback is the rounded value Classic actually painted.
    panel: uiVar('adm-panel', 'rgba(255,255,255,0.04)'),
    accent: uiVar('adm-accent', '#a78bfa'),
};

/**
 * Display type for titles / stat numbers. Classic fallbacks are the CSS
 * initial values (what these elements already computed). The family comes
 * from the scoped Archivo rule in ADMIN_CSS, not a var: no fallback could
 * reproduce "no fontFamily set".
 */
export const ADM_DISPLAY = {
    fontStretch: uiVar('adm-display-stretch', 'normal'),
    textTransform: uiVar('adm-display-case', 'none'),
};

export const ADMIN_CSS =
    `${FL_ANY}{${varBlock(ADMIN_FL_VARS)}}`
    // Archivo for all admin text, except text that set its own family (mono
    // code blocks, icon fonts): StyleSheet families carry an r-fontFamily
    // class, inline ones win over this rule anyway.
    + `${FL_ANY} [data-bo-admin] [dir]:not([class*="r-fontFamily"]){font-family:${ARCHIVO};}`
    + `${FL_ANY} [data-bo-admin] input,${FL_ANY} [data-bo-admin] textarea{font-family:${ARCHIVO};}`
    + `${FL_ANY} [data-bo-admin]{font-variant-numeric:tabular-nums;}`
    + `${FL_ANY} [data-bo-admin] ::selection{background:rgba(167,139,250,0.35);color:#fff;}`
    + `${FL_ANY} [data-bo-admin] :focus-visible{outline:2px solid ${ACCENT};outline-offset:2px;}`;

export function ensureAdminUiStyle() {
    registerUiStyleCss('admin', ADMIN_CSS);
}
